<template>
  <ChannelSection>
    <template #title>
      <slot name="title">{{ sectionLabel }}</slot>
    </template>

    <template #actions>
      <slot name="actions"></slot>

      <DropdownMenuRoot
        v-if="channelStore.permissions.manageShows"
        v-model:open="isOpen"
      >
        <DropdownMenuTrigger>
          <Button
            type="button"
            variant="icon"
            :class="
              newLayout
                ? 'btn-circle border-border-brand size-10'
                : 'btn-circle border-brand-primary size-10'
            "
          >
            <Icon class="text-text size-6" icon="ic:baseline-plus" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuPortal>
          <DropdownMenuContent
            :class="
              newLayout
                ? 'outline-none shadow bg-surface-secondary/90 text-text-secondary backdrop-blur-3xl p-2 rounded-xl will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade min-w-60 z-50 font-sans text-sm'
                : 'outline-none shadow bg-brand-bgdivider-20 backdrop-blur-3xl p-2 rounded-xl will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade min-w-60 z-50 font-sans text-basics-secondary-80 text-sm'
            "
            :side-offset="5"
          >
            <DropdownMenuItem
              :class="
                newLayout
                  ? 'flex items-center select-none outline-none text-text data-[highlighted]:bg-fill-brand data-[highlighted]:text-text-on-brand rounded-md px-2 py-1 gap-2 cursor-pointer'
                  : 'flex items-center select-none outline-none data-[highlighted]:bg-brand-bgdivider-20 data-[highlighted]:text-basics-secondary-80 rounded-md px-2 py-1 gap-2 cursor-pointer'
              "
              @click="onNewShowClick('live')"
            >
              {{ $t("channel.sections.shows.actions.createNewShow") }}
            </DropdownMenuItem>

            <DropdownMenuItem
              :class="
                newLayout
                  ? 'flex items-center select-none outline-none text-text data-[highlighted]:bg-fill-brand data-[highlighted]:text-text-on-brand rounded-md px-2 py-1 gap-2 cursor-pointer'
                  : 'flex items-center select-none outline-none data-[highlighted]:bg-brand-bgdivider-20 data-[highlighted]:text-basics-secondary-80 rounded-md px-2 py-1 gap-2 cursor-pointer'
              "
              @click="onNewShowClick('rtmp')"
            >
              {{ $t("channel.sections.shows.actions.createNewRtmpShow") }}
            </DropdownMenuItem>

            <DropdownMenuItem
              :class="
                newLayout
                  ? 'flex items-center select-none outline-none text-text data-[highlighted]:bg-fill-brand data-[highlighted]:text-text-on-brand rounded-md px-2 py-1 gap-2 cursor-pointer'
                  : 'flex items-center select-none outline-none data-[highlighted]:bg-brand-bgdivider-20 data-[highlighted]:text-basics-secondary-80 rounded-md px-2 py-1 gap-2 cursor-pointer'
              "
              @click="onNewShowClick('upload')"
            >
              {{ $t("channel.sections.shows.actions.uploadShow") }}
            </DropdownMenuItem>

            <DropdownMenuArrow
              :class="
                newLayout
                  ? 'fill-surface-secondary/90 backdrop-blur-3xl'
                  : 'fill-brand-bgdivider-20 backdrop-blur-3xl'
              "
            />
          </DropdownMenuContent>
        </DropdownMenuPortal>
      </DropdownMenuRoot>
    </template>

    <div v-if="!loading" class="relative">
      <ChannelRailVirtualized
        ref="railRef"
        :items="shows"
        :get-key="(show) => show.id"
        :item-width="312"
      >
        <template #default="{ item: { data: show } }">
          <ShowItemCard
            :show="show"
            :is-pinned="channelStore.pinnedShowIds.includes(show.id)"
            @pin-changed="emit('refetch')"
          />
        </template>
      </ChannelRailVirtualized>

      <div
        v-if="loadingMore"
        :class="
          newLayout
            ? 'absolute right-0 top-0 bottom-0 flex items-center justify-center bg-gradient-to-l from-bg via-bg/80 to-transparent pl-8 pr-5'
            : 'absolute right-0 top-0 bottom-0 flex items-center justify-center bg-gradient-to-l from-brand-bg-main via-brand-bg-main/80 to-transparent pl-8 pr-5'
        "
      >
        <Spinner class="size-4" />
      </div>
    </div>

    <SkeletonRail v-else :count="3">
      <template #default="{ index }">
        <ShowCardSkeleton :index="index" />
      </template>
    </SkeletonRail>
  </ChannelSection>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useChannelStore } from "@/stores/channel.store";
import { RouteNames } from "@/router/app.router";
import { type Show, CreateDraftShowDocument } from "@/generated/graphql";
import { useRouter } from "vue-router";
import { useMutation } from "@vue/apollo-composable";
import { useAwaitAction } from "@/composables/use-await-action";
import Button from "@/components/ui/button/button.vue";
import ShowItemCard from "./show-item-card.vue";
import ChannelSection from "../channel-section.vue";
import { Icon } from "@iconify/vue";
import { useInfiniteScroll } from "@vueuse/core";
import {
  DropdownMenuArrow,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from "reka-ui";
import ChannelRailVirtualized from "../channel-rail-virtualized.vue";
import SkeletonRail from "@/components/ui/skeleton/skeleton-rail.vue";
import ShowCardSkeleton from "@/components/ui/skeleton/show-card-skeleton.vue";
import Spinner from "@/components/ui/spinner.vue";
import { useAppStore } from "@/stores/app.store";
import { useI18n } from "vue-i18n";
import { useFeatureFlags } from "@/composables/use-feature-flags";
const appStore = useAppStore();
const { getBoolean } = useFeatureFlags();
const newLayout = getBoolean("enable_new_channel_layout");
const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    shows: Show[];
    loading?: boolean;
    loadingMore?: boolean;
    /**
     * Whether more pages remain to load. Gates the infinite-scroll trigger so
     * an empty / short rail (no scrollable overflow) does not emit `loadMore`
     * every `interval`ms forever. Defaults to true so callers that omit it keep
     * the prior (ungated) behaviour.
     */
    hasMore?: boolean;
  }>(),
  { hasMore: true },
);

const emit = defineEmits<{
  loadMore: [];
  refetch: [];
}>();

const channelStore = useChannelStore();
const router = useRouter();
const isOpen = ref(false);
const railRef = ref<HTMLElement | null>(null);

const { mutate: createDraftShow } = useMutation(CreateDraftShowDocument);

const { handler: onNewShowClick } = useAwaitAction(
  async (type: "live" | "upload" | "rtmp") => {
    isOpen.value = false;
    const show = await createDraftShow({
      channel_id: channelStore.channel!.id,
      uses_rtmp: type === "rtmp",
    });

    router.push({
      name: RouteNames.NewShow,
      params: {
        show: show?.data?.createDraftShow.id,
        channel: channelStore.channelSlug,
        type,
      },
    });
  },
);

useInfiniteScroll(
  railRef,
  () => {
    emit("loadMore");
  },
  {
    direction: "right" as const,
    distance: 100,
    interval: 300,
    canLoadMore: () => props.hasMore && !props.loadingMore,
  },
);

const sectionLabel = computed(() => {
  if (appStore.isEmbed) {
    return t("channel.titles.embedShows");
  }
  return t("channel.titles.featuredShows");
});
</script>
